import { useEffect, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Paper, Button, Alert, Stack, CircularProgress, Grid2 as Grid,
} from "@mui/material";
import { useForm, type DefaultValues, type UseFormReturn, type FieldValues } from "react-hook-form";
import { useQuery, useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";

/**
 * Shared create/edit scaffold for the diagnostics masters. One component serves both routes — `id`
 * absent is a create, present is an edit — so field rules can't drift between them. Callers supply the
 * field layout via `children`; everything around it (permission gate, load, submit, navigation) is here.
 */
export function MasterFormPage<TValues extends FieldValues, TDto>({
  breadcrumbs, noun, basePath, listQueryKey, detailQueryKey,
  defaultValues, getFn, toValues, createFn, updateFn, children, maxWidth = 720,
}: {
  breadcrumbs: string[];
  /** Singular noun, e.g. "specimen" — used for the title and toast. */
  noun: string;
  basePath: string;
  listQueryKey: QueryKey;
  /** Builder for the detail cache key, e.g. id => ["dx-specimen", id]. */
  detailQueryKey: (id: string) => QueryKey;
  defaultValues: DefaultValues<TValues>;
  getFn: (id: string) => Promise<TDto>;
  /** Maps the loaded record onto form values. */
  toValues: (dto: TDto) => TValues;
  createFn: (v: TValues) => Promise<unknown>;
  updateFn: (id: string, v: TValues) => Promise<unknown>;
  children: (form: UseFormReturn<TValues>, isEdit: boolean) => ReactNode;
  maxWidth?: number;
}) {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("diagnostics.catalog.manage"));

  const form = useForm<TValues>({ defaultValues });
  const { handleSubmit, reset } = form;

  const existing = useQuery({
    queryKey: detailQueryKey(id ?? ""),
    queryFn: () => getFn(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing.data) reset(toValues(existing.data));
    // `toValues` is defined inline by callers; re-running on identity changes would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data, reset]);

  const title = `${isEdit ? "Edit" : "New"} ${noun}`;

  const save = useMutation({
    meta: { successMessage: `${noun[0].toUpperCase()}${noun.slice(1)} ${isEdit ? "updated" : "created"}` },
    mutationFn: (v: TValues) => isEdit ? updateFn(id!, v) : createFn(v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listQueryKey });
      if (isEdit) qc.invalidateQueries({ queryKey: detailQueryKey(id!) });
      navigate(basePath);
    },
  });

  if (!canManage) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title={title} />
        <Alert severity="warning">You lack the diagnostics.catalog.manage permission.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader breadcrumbs={breadcrumbs} title={title} />
      <Paper sx={{ p: 3, maxWidth }}>
        {existing.isLoading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={16} /><span>Loading…</span>
          </Stack>
        )}
        {existing.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(existing.error)}</Alert>}
        {save.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(save.error)}</Alert>}

        <form onSubmit={handleSubmit(v => save.mutate(v))}>
          <Grid container spacing={2}>
            {children(form, isEdit)}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" disabled={save.isPending || existing.isLoading}
                  startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
                  Save
                </Button>
                <Button onClick={() => navigate(basePath)}>Cancel</Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
